/**
 * printDailySummary.js
 * ---------------------------------------------------------------
 * Daily Summary report ka printable HTML banata hai aur use
 * browser ke print dialog (hidden iframe) se print karta hai.
 * Capacitor native printer plugin ka koi use nahi — sirf web print.
 * ---------------------------------------------------------------
 */

/* ----------------------------- helpers ----------------------------- */

export const STATUS_LABEL = {
  0: "Registered",
  1: "Confirmed",
  2: "Cancelled",
  3: "Pending",
};

export const statusLabel = (s) =>
  STATUS_LABEL[s] !== undefined ? STATUS_LABEL[s] : `Status ${s}`;

export const money = (n) =>
  "\u20B9" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const num = (n) => Number(n || 0);

const bondOf = (row) => String(row?.patientBond || "").trim().toLowerCase();

const esc = (v) =>
  String(v === null || v === undefined ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** yyyy-MM-dd -> dd MMM yyyy (jo mile wahi wapas de deta hai agar parse na ho) */
export const prettyDate = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return String(d);
  return t.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* --------------------------- calculations --------------------------- */

/**
 * API response se saare totals nikaalta hai.
 * @param {{registered?: Array, confirmed?: Array, result?: object}} data
 */
export function summarize(data, statusName = statusLabel) {
  const registered = Array.isArray(data?.registered) ? data.registered : [];
  const confirmed = Array.isArray(data?.confirmed) ? data.confirmed : [];

  const sum = (arr, pick) => arr.reduce((s, x) => s + num(pick(x)), 0);

  const cashRows = confirmed.filter((c) => bondOf(c) === "cash");
  const upiRows = confirmed.filter((c) => bondOf(c) === "upi");
  const otherRows = confirmed.filter(
    (c) => bondOf(c) !== "cash" && bondOf(c) !== "upi"
  );

  const totalAmount = sum(confirmed, (c) => c.total);
  const totalCash = sum(cashRows, (c) => c.total);
  const totalUpi = sum(upiRows, (c) => c.total);
  const totalOther = sum(otherRows, (c) => c.total);

  // patientType wise breakup
  const byTypeMap = new Map();
  confirmed.forEach((c) => {
    const key = c.patientType || "—";
    const row = byTypeMap.get(key) || {
      patientType: key,
      count: 0,
      cash: 0,
      upi: 0,
      other: 0,
      total: 0,
    };
    row.count += num(c.count);
    row.total += num(c.total);
    if (bondOf(c) === "cash") row.cash += num(c.total);
    else if (bondOf(c) === "upi") row.upi += num(c.total);
    else row.other += num(c.total);
    byTypeMap.set(key, row);
  });

  // status wise registered
  const byStatus = registered.map((r) => ({
    ...r,
    label: statusName(r.status),
    count: num(r.count),
  }));

  return {
    registered,
    confirmed,
    byType: Array.from(byTypeMap.values()).sort((a, b) => b.total - a.total),
    byStatus,
    registeredCount: sum(registered, (r) => r.count),
    confirmedCount: sum(confirmed, (c) => c.count),
    cashCount: sum(cashRows, (c) => c.count),
    upiCount: sum(upiRows, (c) => c.count),
    otherCount: sum(otherRows, (c) => c.count),
    totalAmount,
    totalCash,
    totalUpi,
    totalOther,
    cashShare: totalAmount ? (totalCash / totalAmount) * 100 : 0,
    upiShare: totalAmount ? (totalUpi / totalAmount) * 100 : 0,
    otherShare: totalAmount ? (totalOther / totalAmount) * 100 : 0,
  };
}

/* ---------------------------- print HTML ---------------------------- */

/**
 * @param {object} opts
 * @param {object} opts.data      API ka response body
 * @param {object} opts.params    { centerId, ForDate, Normal, NewEntry }
 * @param {string} [opts.title]   Report heading
 * @param {string} [opts.brand]   Center / company name
 */
export function buildDailySummaryHtml({
  data,
  params = {},
  title = "Daily Summary Report",
  brand = "Daily Collection Register",
  statusName = statusLabel,
}) {
  const s = summarize(data, statusName);
  const printedAt = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const metaChips = [
    ["Center", params.centerName || "—"],
    ["For date", prettyDate(params.ForDate)],
    ["Normal rate", params.Normal ? money(params.Normal) : "—"],
    ["New entry rate", params.NewEntry ? money(params.NewEntry) : "—"],
  ]
    .map(
      ([k, v]) =>
        `<div class="chip"><span class="chip-k">${esc(k)}</span><span class="chip-v">${esc(v)}</span></div>`
    )
    .join("");

  const registeredRows = s.byStatus.length
    ? s.byStatus
        .map(
          (r) => `<tr>
            <td>${esc(r.label)}</td>
            <td class="c">${esc(r.status)}</td>
            <td class="r b">${esc(r.count)}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="3" class="empty">Koi registration record nahi mila</td></tr>`;

  const confirmedRows = s.confirmed.length
    ? s.confirmed
        .map(
          (c, i) => `<tr>
            <td class="c">${i + 1}</td>
            <td>${esc(c.patientType)}</td>
            <td><span class="tag tag-${bondOf(c) === "upi" ? "upi" : bondOf(c) === "cash" ? "cash" : "other"}">${esc(c.patientBond)}</span></td>
            <td class="r">${esc(c.count)}</td>
            <td class="r b">${money(c.total)}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">Koi confirmed entry nahi mili</td></tr>`;

  const typeRows = s.byType
    .map(
      (t) => `<tr>
        <td>${esc(t.patientType)}</td>
        <td class="r">${t.count}</td>
        <td class="r">${money(t.cash)}</td>
        <td class="r">${money(t.upi)}</td>
        <td class="r b">${money(t.total)}</td>
      </tr>`
    )
    .join("");

  const splitBar = s.totalAmount
    ? `<div class="split">
         <div class="split-bar">
           <span style="width:${s.cashShare.toFixed(2)}%" class="sp-cash"></span>
           <span style="width:${s.upiShare.toFixed(2)}%" class="sp-upi"></span>
           <span style="width:${s.otherShare.toFixed(2)}%" class="sp-other"></span>
         </div>
         <div class="split-legend">
           <em><i class="dot dot-cash"></i>Cash ${s.cashShare.toFixed(1)}%</em>
           <em><i class="dot dot-upi"></i>UPI ${s.upiShare.toFixed(1)}%</em>
           ${s.totalOther ? `<em><i class="dot dot-other"></i>Other ${s.otherShare.toFixed(1)}%</em>` : ""}
         </div>
       </div>`
    : "";

  const apiNote =
    data?.result && data.result.succeeded === false
      ? `<p class="warn">API message: ${esc(data.result.message || "Request failed")}</p>`
      : "";

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(title)} — ${esc(prettyDate(params.ForDate))}</title>
<style>
  @page { size: A4; margin: 12mm 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body {
    margin: 0; padding: 0 4px;
    font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #0f172a; font-size: 12px; line-height: 1.45;
  }
  .sheet { max-width: 780px; margin: 0 auto; }

  header.top {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
    padding: 14px 16px; border-radius: 12px;
    background: linear-gradient(120deg, #0369a1 0%, #0284c7 55%, #06b6d4 100%);
    color: #fff;
  }
  .brand { font-size: 17px; font-weight: 700; letter-spacing: .2px; }
  .brand small { display:block; font-weight: 500; font-size: 11px; opacity: .85; margin-top: 2px; letter-spacing:.4px; text-transform: uppercase; }
  .stamp { text-align: right; font-size: 10.5px; opacity: .92; }
  .stamp b { display:block; font-size: 14px; }

  .chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 14px; }
  .chip { border: 1px solid #bae6fd; background: #f0f9ff; border-radius: 8px; padding: 5px 10px; }
  .chip-k { display:block; font-size: 9px; text-transform: uppercase; letter-spacing: .6px; color: #0369a1; }
  .chip-v { font-weight: 700; color: #0c4a6e; }

  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; }
  .kpi { border: 1px solid #e2e8f0; border-top: 3px solid #0ea5e9; border-radius: 10px; padding: 8px 10px; }
  .kpi.cash { border-top-color: #0d9488; }
  .kpi.upi { border-top-color: #6366f1; }
  .kpi.grand { border-top-color: #0369a1; background: #f0f9ff; }
  .kpi span { display:block; font-size: 9.5px; text-transform: uppercase; letter-spacing: .6px; color: #64748b; }
  .kpi b { font-size: 15px; color: #0f172a; }
  .kpi i { font-style: normal; font-size: 10px; color: #94a3b8; }

  .split { margin: -4px 0 16px; }
  .split-bar { display: flex; height: 9px; border-radius: 999px; overflow: hidden; background: #e2e8f0; }
  .sp-cash { background: #0d9488; } .sp-upi { background: #6366f1; } .sp-other { background: #94a3b8; }
  .split-legend { display: flex; gap: 14px; margin-top: 6px; font-size: 10px; color: #475569; }
  .split-legend em { font-style: normal; display: inline-flex; align-items: center; gap: 5px; }
  .dot { width: 8px; height: 8px; border-radius: 2px; display:inline-block; }
  .dot-cash { background:#0d9488; } .dot-upi { background:#6366f1; } .dot-other { background:#94a3b8; }

  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: 1.1px; color: #0369a1;
       margin: 16px 0 7px; padding-bottom: 4px; border-bottom: 1.5px solid #bae6fd; }
  table { width: 100%; border-collapse: collapse; }
  thead th { background: #e0f2fe; color: #0c4a6e; font-size: 9.5px; text-transform: uppercase;
             letter-spacing: .6px; text-align: left; padding: 7px 8px; border-bottom: 1px solid #bae6fd; }
  tbody td { padding: 6px 8px; border-bottom: 1px solid #eef2f7; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  tfoot td { padding: 8px; background: #0c4a6e; color: #fff; font-weight: 700; }
  .c { text-align: center; } .r { text-align: right; } .b { font-weight: 700; }
  .empty { text-align: center; color: #94a3b8; padding: 14px 0; }
  .tag { display:inline-block; padding: 1px 7px; border-radius: 999px; font-size: 10px; font-weight: 600; }
  .tag-cash { background:#ccfbf1; color:#0f766e; }
  .tag-upi { background:#e0e7ff; color:#4338ca; }
  .tag-other { background:#e2e8f0; color:#475569; }
  .warn { background:#fff7ed; border:1px solid #fed7aa; color:#9a3412; padding:7px 10px; border-radius:8px; }

  .totals { margin-top: 16px; display: grid; grid-template-columns: 1fr 240px; gap: 16px; align-items: end; }
  .sign { border-top: 1px dashed #94a3b8; padding-top: 6px; font-size: 10.5px; color: #64748b; max-width: 220px; }
  .totbox { border: 1.5px solid #0284c7; border-radius: 10px; overflow: hidden; }
  .totbox div { display: flex; justify-content: space-between; padding: 6px 10px; font-size: 11.5px; }
  .totbox div + div { border-top: 1px solid #e0f2fe; }
  .totbox .grand { background: #0284c7; color: #fff; font-size: 13.5px; font-weight: 700; border-top: none; }

  footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e2e8f0;
           font-size: 9.5px; color: #94a3b8; display:flex; justify-content: space-between; }

  @media print { body { padding: 0; } .sheet { max-width: none; } tr, .kpi, .totbox { break-inside: avoid; } }
</style>
</head>
<body>
<div class="sheet">
  <header class="top">
    <div class="brand">${esc(brand)}<small>${esc(title)}</small></div>
    <div class="stamp"><b>${esc(prettyDate(params.ForDate))}</b>Printed: ${esc(printedAt)}</div>
  </header>

  <div class="chips">${metaChips}</div>
  ${apiNote}

  <div class="kpis">
    <div class="kpi"><span>Registered</span><b>${s.registeredCount}</b><i>${s.byStatus.length} status group</i></div>
    <div class="kpi"><span>Confirmed</span><b>${s.confirmedCount}</b><i>${s.confirmed.length} entries</i></div>
    <div class="kpi cash"><span>Total Cash</span><b>${money(s.totalCash)}</b><i>${s.cashCount} patients</i></div>
    <div class="kpi upi"><span>Total UPI</span><b>${money(s.totalUpi)}</b><i>${s.upiCount} patients</i></div>
  </div>
  ${splitBar}

  <h2>Registration status</h2>
  <table>
    <thead><tr><th>Status</th><th class="c">Code</th><th class="r">Count</th></tr></thead>
    <tbody>${registeredRows}</tbody>
    <tfoot><tr><td colspan="2">Total registered</td><td class="r">${s.registeredCount}</td></tr></tfoot>
  </table>

  <h2>Confirmed collection — line items</h2>
  <table>
    <thead><tr><th class="c">#</th><th>Patient type</th><th>Mode</th><th class="r">Count</th><th class="r">Amount</th></tr></thead>
    <tbody>${confirmedRows}</tbody>
    <tfoot><tr><td colspan="3">Grand total</td><td class="r">${s.confirmedCount}</td><td class="r">${money(s.totalAmount)}</td></tr></tfoot>
  </table>

  ${
    s.byType.length
      ? `<h2>Patient type wise</h2>
  <table>
    <thead><tr><th>Patient type</th><th class="r">Count</th><th class="r">Cash</th><th class="r">UPI</th><th class="r">Total</th></tr></thead>
    <tbody>${typeRows}</tbody>
  </table>`
      : ""
  }

  <div class="totals">
    <div class="sign">Verified by / Signature</div>
    <div class="totbox">
      <div><span>Total Cash</span><b>${money(s.totalCash)}</b></div>
      <div><span>Total UPI</span><b>${money(s.totalUpi)}</b></div>
      ${s.totalOther ? `<div><span>Other modes</span><b>${money(s.totalOther)}</b></div>` : ""}
      <div class="grand"><span>Total Amount</span><span>${money(s.totalAmount)}</span></div>
    </div>
  </div>

  <footer>
    <span>${params.centerName ? esc(params.centerName) + " &middot; " : ""}${esc(prettyDate(params.ForDate))}</span>
    <span>System generated report</span>
  </footer>
</div>
</body>
</html>`;
}

/* ------------------------------ printing ------------------------------ */

/**
 * HTML ko hidden iframe me daal kar browser ka print dialog kholta hai.
 * @returns {Promise<"browser"|"unsupported">}
 */
export async function printHtml(html, name = "daily-summary") {
  return await new Promise((resolve) => {
    // purana bacha hua frame hata do
    const old = document.getElementById("__print_frame__");
    if (old) old.remove();

    const frame = document.createElement("iframe");
    frame.id = "__print_frame__";
    frame.title = name;
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    // NOTE: size 0 ya display:none rakhne par Chrome blank page print karta hai,
    // isliye poora A4 size dekar screen ke bahar bhej rahe hain.
    Object.assign(frame.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: "820px",
      height: "1200px",
      border: "0",
      visibility: "visible",
    });

    document.body.appendChild(frame);

    const w = frame.contentWindow;
    const doc = w.document;
    doc.open();
    doc.write(html);
    doc.close();

    // IMPORTANT: sirf ek baar print hona chahiye.
    // (iframe ka load event do baar aata hai — about:blank + document.write —
    //  isliye onload par print karne se do print dialog khul jaate the.)
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      try {
        if (!frame.contentWindow || typeof frame.contentWindow.print !== "function") {
          frame.remove();
          return resolve("unsupported");
        }
        // frame ko dialog band hone ke baad hi hatana hai,
        // pehle hata diya to preview blank aa jata hai.
        frame.contentWindow.onafterprint = () => setTimeout(() => frame.remove(), 500);
        frame.contentWindow.focus();
        frame.contentWindow.print();
        resolve("browser");
        setTimeout(() => frame.remove(), 60000); // safety cleanup
      } catch (e) {
        frame.remove();
        resolve("unsupported");
      }
    };

    // layout + fonts settle hone ka thoda time
    if (doc.fonts && doc.fonts.ready) {
      doc.fonts.ready.then(() => setTimeout(fire, 120)).catch(() => setTimeout(fire, 250));
      setTimeout(fire, 1500); // agar fonts.ready kabhi resolve na ho
    } else {
      setTimeout(fire, 250);
    }
  });
}

/** Card se seedha call karne ke liye single entry point */
export async function printDailySummary(opts) {
  const html = buildDailySummaryHtml(opts);
  const fileName = `daily-summary-${opts?.params?.ForDate || "report"}`;
  return await printHtml(html, fileName);
}